import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  Pressable,
  Modal,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import UserAvatar from './UserAvatar';

export interface UserMenuItem {
  id: string;
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  onPress: () => void;
}

export interface UserMenuProps {
  onProfilePress: () => void;
  onSettingsPress: () => void;
  onInvitePress: () => void;
  onLogout: () => void;
  /** Optional custom trigger (default: UserAvatar) */
  renderTrigger?: (props: { onPress: () => void }) => React.ReactNode;
  testID?: string;
  /** Force dropdown (web) or sheet (mobile) for testing */
  variant?: 'dropdown' | 'sheet';
}

const MENU_ITEMS_CONFIG = (handlers: {
  onProfilePress: () => void;
  onSettingsPress: () => void;
  onInvitePress: () => void;
  onLogout: () => void;
}): UserMenuItem[] => [
  {
    id: 'profile',
    label: 'View profile',
    icon: 'person-outline',
    onPress: handlers.onProfilePress,
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings-outline',
    onPress: handlers.onSettingsPress,
  },
  {
    id: 'invite',
    label: 'Invite',
    icon: 'share-social-outline',
    onPress: handlers.onInvitePress,
  },
  {
    id: 'logout',
    label: 'Logout',
    icon: 'log-out-outline',
    onPress: handlers.onLogout,
  },
];

const UserMenu: React.FC<UserMenuProps> = ({
  onProfilePress,
  onSettingsPress,
  onInvitePress,
  onLogout,
  renderTrigger,
  testID,
  variant,
}) => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<View>(null);

  const screenWidth = Dimensions.get('window').width;
  const isWeb = Platform.OS === 'web';
  const isWebLayout = isWeb && screenWidth >= 768;
  const useDropdown = variant ? variant === 'dropdown' : isWebLayout;

  const menuItems = MENU_ITEMS_CONFIG({
    onProfilePress: () => {
      setOpen(false);
      onProfilePress();
    },
    onSettingsPress: () => {
      setOpen(false);
      onSettingsPress();
    },
    onInvitePress: () => {
      setOpen(false);
      onInvitePress();
    },
    onLogout: () => {
      setOpen(false);
      onLogout();
    },
  });

  const handleTriggerPress = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  // Close dropdown on outside click (web)
  useEffect(() => {
    if (!useDropdown || !open || !isWeb) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current) {
        // @ts-expect-error - contains is available on web (RN Web View ref)
        if (!containerRef.current.contains(target)) {
          setOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [useDropdown, open, isWeb]);

  // Close dropdown on Escape (web)
  useEffect(() => {
    if (!useDropdown || !open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [useDropdown, open]);

  if (!user) return null;

  const trigger =
    renderTrigger != null ? (
      renderTrigger({ onPress: handleTriggerPress })
    ) : (
      <Pressable
        onPress={handleTriggerPress}
        accessibilityLabel="Open account menu"
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        testID={testID}
        style={({ pressed }) => [
          styles.trigger,
          pressed && styles.triggerPressed,
        ]}
      >
        <UserAvatar
          size={useDropdown ? 40 : 32}
          imageUrl={user.profileImageUrl}
          name={user.name}
          email={user.email}
          showActiveStatus
          isActive
        />
      </Pressable>
    );

  return (
    <View ref={containerRef} style={styles.wrapper} collapsable={false}>
      {trigger}

      {useDropdown && open && (
        <View
          style={[
            styles.dropdown,
            {
              backgroundColor: colors.card ?? colors.background,
              borderColor: colors.border,
            },
          ]}
          accessibilityRole="menu"
          accessibilityLabel="Account menu"
        >
          {menuItems.map((item) => (
            <Pressable
              key={item.id}
              style={({ pressed }) => [
                styles.dropdownItem,
                {
                  backgroundColor: pressed
                    ? colors.primary + '15'
                    : 'transparent',
                },
              ]}
              onPress={item.onPress}
            >
              <Ionicons
                name={item.icon}
                size={20}
                color={colors.text}
                style={styles.dropdownItemIcon}
              />
              <Text style={[styles.dropdownItemLabel, { color: colors.text }]}>
                {item.label}
              </Text>
            </Pressable>
          ))}
        </View>
      )}

      {!useDropdown && (
        <Modal
          visible={open}
          transparent
          animationType="slide"
          presentationStyle="pageSheet"
          onRequestClose={close}
          statusBarTranslucent
        >
          <Pressable style={styles.sheetOverlay} onPress={close} />
          <View
            style={[
              styles.sheet,
              {
                backgroundColor: colors.background ?? colors.card,
                borderTopColor: colors.border,
              },
            ]}
            accessibilityRole="menu"
            accessibilityLabel="Account menu"
          >
            <View
              style={[styles.sheetHandle, { backgroundColor: colors.border }]}
            />
            {menuItems.map((item) => (
              <TouchableOpacity
                key={item.id}
                style={[styles.sheetItem, { borderBottomColor: colors.border }]}
                onPress={item.onPress}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={item.label}
              >
                <Ionicons
                  name={item.icon}
                  size={24}
                  color={colors.text}
                  style={styles.sheetItemIcon}
                />
                <Text style={[styles.sheetItemLabel, { color: colors.text }]}>
                  {item.label}
                </Text>
                <Ionicons
                  name="chevron-forward"
                  size={20}
                  color={colors.secondary}
                />
              </TouchableOpacity>
            ))}
          </View>
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative',
  },
  trigger: {
    padding: 4,
  },
  triggerPressed: {
    opacity: 0.9,
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    minWidth: 180,
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 1100,
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 8,
      },
    }),
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  dropdownItemIcon: {
    marginRight: 12,
  },
  dropdownItemLabel: {
    fontSize: 15,
    fontWeight: '500',
  },
  sheetOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  sheet: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderTopWidth: 1,
    paddingBottom: 34,
    paddingTop: 12,
  },
  sheetHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  sheetItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 44,
    borderBottomWidth: 1,
  },
  sheetItemIcon: {
    marginRight: 16,
  },
  sheetItemLabel: {
    flex: 1,
    fontSize: 17,
    fontWeight: '500',
  },
});

export default UserMenu;
